<template xmlns:v-bind="http://www.w3.org/1999/xhtml" xmlns:v-el="http://www.w3.org/1999/xhtml">
  <div class="map-container">
    <div
      v-show="isLoading"
      class="loader-container">
      <div>
        <loader-small />
        {{ capitalize($t('states.loading')) }}
      </div>
    </div>
    <div
      :id="id"
      class="map" />
    <div
      id="inventory_cluster_popup_content"
      style="display: none">
      <div class="site-popup-content">
        <div v-if="currentClusterInventory">
          <div class="title">
            <strong
              >{{ currentClusterInventory.length }} {{ $t('names.screen', currentClusterInventory.length) }}</strong
            >
          </div>
          <hr />
          <div class="inventory-table-container">
            <table>
              <tbody>
                <tr
                  v-for="inventory in currentClusterInventory"
                  :key="inventory.name">
                  <td :title="inventory">
                    {{ inventory.name }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script type="text/babel">
  import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';

  import geojsonExtent from '@mapbox/geojson-extent';
  import MapboxDraw from '@mapbox/mapbox-gl-draw';
  import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
  import { DateTime } from 'luxon';
  import mapboxgl from 'mapbox-gl';
  import Supercluster from 'supercluster';
  import { capitalize } from 'vue';

  import Colors from '@/Colors';
  import { getContainerHeight, getContainerWidth } from '@/globals';
  import MapPointControl from '@/org-app/components/geofenceGroups/MapPointControl';
  import MapPolygonControl from '@/org-app/components/geofenceGroups/MapPolygonControl';
  import MapStyleControl from '@/org-app/components/geofenceGroups/MapStyleControl';
  import i18n from '@/vueI18n';

  import LoaderSmall from './LoaderSmall';

  export default {
    components: {
      LoaderSmall,
    },
    props: {
      id: {
        type: String,
        default: 'inventorymapInternal',
      },
      geocodedLocations: {
        type: Array,
      },
      isLoading: {
        type: Boolean,
        default: false,
      },
      enableMapStyleControl: {
        type: Boolean,
        default: false,
      },
      enableMapPointControl: {
        type: Boolean,
        default: false,
      },
      enableMapPolygonControl: {
        type: Boolean,
        default: false,
      },
      allowMouseScrollZooming: {
        type: Boolean,
        default: false,
      },
      fitToBounds: {
        type: Boolean,
        default: true,
      },
      pitch: {
        type: Number,
        default: null,
      },
      searchProximity: {
        type: Object,
        default: null,
      },
      initialPolygon: {
        type: Object,
        default: null,
      },
      geofenceGroups: {
        type: Array,
        default: null,
      },
      locationShapes: {
        type: Array,
        default: null,
      },
    },
    data() {
      return {
        clusterRadius: 40,
        geocoder: null,
        initialPolygonMapboxDraw: null,
        isMapLoaded: false,
        map: null,
        mapboxDraw: null,
        mapPointControl: null,
        mapPolygonControl: null,
        mode: '',
        superC: null,
        lastPopup: null,
        currentClusterInventory: [],
      };
    },
    computed: {
      geocodedLocationsGeoJson() {
        return {
          type: 'FeatureCollection',
          crs: {
            type: 'name',
            properties: {
              name: 'urn:ogc:def:crs:OGC:1.3:CRS84',
            },
          },

          features: this.geocodedLocations.map((x) => {
            return {
              type: 'Feature',
              properties: {
                id: x.id,
                name: x.name,
                type: x.type,
              },
              geometry: { type: 'Point', coordinates: [x.lng, x.lat] },
            };
          }),
        };
      },
      shapesBoundingBox() {
        const data = {
          type: 'FeatureCollection',
          features: this.locationShapes != null ? this.locationShapes.map((x) => x.feature) : [],
        };
        if (data == null || data.features == null || data.features.length === 0) {
          return null;
        }
        const extents = data.features.map((x) => geojsonExtent(x.geometry));

        const minLat = extents.map((x) => x[1]).reduce((a, b) => Math.min(a, b), 90);
        const maxLat = extents.map((x) => x[3]).reduce((a, b) => Math.max(a, b), -90);
        const minLng = extents.map((x) => x[0]).reduce((a, b) => Math.min(a, b), 180);
        const maxLng = extents.map((x) => x[2]).reduce((a, b) => Math.max(a, b), -180);

        return [
          [minLng, minLat],
          [maxLng, maxLat],
        ];
      },
      inventoryBoundingBox() {
        if (this.geocodedLocations == null || this.geocodedLocations.length === 0) {
          return null;
        }
        const groupedCoords = Array.from(new Set(this.geocodedLocations.map((x) => `${x.lng}|${x.lat}`)));
        if (groupedCoords.length > 1) {
          return [
            [
              Math.min(...this.geocodedLocations.map((x) => x.lng)),
              Math.min(...this.geocodedLocations.map((x) => x.lat)),
            ],
            [
              Math.max(...this.geocodedLocations.map((x) => x.lng)),
              Math.max(...this.geocodedLocations.map((x) => x.lat)),
            ],
          ];
        }
        if (this.geocodedLocations.length === 1 && this.geocodedLocations[0].geofenceBoundingBox != null) {
          return this.geocodedLocations[0].geofenceBoundingBox;
        }

        const coord = groupedCoords[0].split('|');
        return [
          [coord[0], coord[1]],
          [coord[0], coord[1]],
        ];
      },
      boundingBox() {
        if (this.shapesBoundingBox == null && this.inventoryBoundingBox == null) {
          return null;
        }
        if (this.shapesBoundingBox == null) {
          return this.inventoryBoundingBox;
        }
        if (this.inventoryBoundingBox == null) {
          return this.shapesBoundingBox;
        }
        const s = this.shapesBoundingBox;
        const i = this.inventoryBoundingBox;
        return [
          [Math.min(s[0][0], i[0][0]), Math.min(s[0][1], i[0][1])],
          [Math.max(s[1][0], i[1][0]), Math.max(s[1][1], i[1][1])],
        ];
      },
    },
    watch: {
      geocodedLocations(val) {
        if (!this.isMapLoaded || val == null) {
          return;
        }
        this.refreshClusters(false);
      },
      searchProximity(val) {
        if (this.geocoder != null) {
          this.geocoder.setProximity(val);
        }
      },
      initialPolygon() {
        this.updateDeleteGeofenceButtonVisibility();
      },
      geofenceGroups() {
        this.updateGeofenceLayerData();
      },
      locationShapes() {
        this.updateLocationShapesDataSource(true);
      },
      boundingBox() {
        this.zoomToBoundingBox();
      },
    },
    mounted() {
      setTimeout(() => {
        const mapOptions = {
          container: this.id,
          style: 'mapbox://styles/mapbox/light-v9?optimize=true',
          minzoom: 1.3,
          zoom: 1,
          pitch: this.pitch,
        };
        this.map = this.initMap(mapOptions);
      }, 1000);
    },
    methods: {
      capitalize,
      initMap(mapOptions) {
        const map = new mapboxgl.Map(mapOptions);
        map.on('load', () => {
          if (this.enableMapStyleControl) {
            this.mapStyleControl = new MapStyleControl();
            this.mapStyleControl.clickHandler = (isSatellite) => {
              if (isSatellite) {
                this.changeMapStyleToSatellite();
              } else {
                this.changeMapStyleToLight();
              }
            };
            this.map.addControl(this.mapStyleControl);
          }
          if (this.enableMapPointControl) {
            this.mapPointControl = new MapPointControl();
            this.mapPointControl.clickHandler = () => {
              this.mode = 'create-point';
              this.map.getCanvas().style.cursor = 'crosshair';
            };
            this.map.addControl(this.mapPointControl);
          }
          if (this.enableMapPolygonControl) {
            this.mapPolygonControl = new MapPolygonControl();
            this.mapPolygonControl.clickHandler = () => {
              this.mapboxDraw.changeMode('draw_polygon');
              this.mode = null;
              this.mapPointControl.deactivate();
            };
            this.map.addControl(this.mapPolygonControl);
            this.mapboxDraw = new MapboxDraw({
              displayControlsDefault: false,
              controls: {
                trash: true,
                fullscreen: false,
              },
              clickBuffer: 4,
            });

            this.map.on('draw.create', (e) => {
              this.handlePolygonCreation(e);
            });

            this.map.on('draw.delete', (e) => {
              this.handlePolygonDeletion(e);
            });

            this.map.on('draw.update', (e) => {
              this.handlePolygonUpdate(e);
            });

            this.map.addControl(this.mapboxDraw);

            //
            // For some reason MapboxDraw does not seem active until you click on the polygon button,
            // the following will force it to become active (blue outlines on polygons).
            //
            setTimeout(() => {
              this.updateDeleteGeofenceButtonVisibility();

              this.mapboxDraw.changeMode('draw_polygon');
              setTimeout(() => {
                this.mapboxDraw.changeMode('simple_select');

                if (this.initialPolygon != null) {
                  this.initialPolygonMapboxDraw = this.mapboxDraw.add({
                    id: `${DateTime.local().toSeconds()}`,
                    type: 'Feature',
                    geometry: this.initialPolygon,
                    properties: {},
                  });
                }
              }, 100);
            }, 1000);
          } else if (this.initialPolygon != null) {
            const feature = {
              type: 'Feature',
              geometry: this.initialPolygon.geo_json,
              properties: {},
            };
            this.map.addLayer({
              id: 'polygon-fill',
              type: 'fill',
              source: {
                type: 'geojson',
                data: feature,
              },
              paint: {
                'fill-opacity': 0.3,
                'fill-color': Colors.geofences,
              },
            });
            this.map.addLayer({
              id: 'polygon-line',
              type: 'line',
              source: {
                type: 'geojson',
                data: feature,
              },
              paint: {
                'line-color': Colors.geofences,
              },
            });
          }

          this.isMapLoaded = true;
          this.refreshClusters(true);
        });

        map.on('click', (e) => {
          if (this.mode === 'create-point') {
            this.mode = '';
            this.map.getCanvas().style.cursor = '';
            this.mapPointControl.deactivate();
            this.$emit('pointClicked', [e.lngLat.lng, e.lngLat.lat]);
          }
        });

        this.geocoder = new MapboxGeocoder({
          accessToken: mapboxgl.accessToken,
          placeholder: i18n.global.t('actions.search'),
          mapboxgl: map,
        });

        this.geocoder.on('result', (e) => {
          this.$emit('pointClicked', e.result.center);
        });

        map.addControl(this.geocoder);
        map.addControl(new mapboxgl.NavigationControl());
        map.addControl(new mapboxgl.ScaleControl());

        return map;
      },
      changeMapStyleToSatellite() {
        this.mapStyle = 'mapbox://styles/mapbox/satellite-streets-v9?optimize=true';
        this.map.setStyle(this.mapStyle);
        setTimeout(() => {
          this.refreshClusters();
        }, 1000);
      },
      changeMapStyleToLight() {
        this.mapStyle = 'mapbox://styles/mapbox/light-v9?optimize=true';
        this.map.setStyle(this.mapStyle);
        setTimeout(() => {
          this.refreshClusters();
        }, 1000);
      },
      refreshClusters(forceFitToBounds = false) {
        if (this.refreshClusters != null) {
          clearTimeout(this.refreshClustersTimer);
        }
        this.refreshClustersTimer = setTimeout(() => {
          this.refreshClustersTimer = null;
          if (this.map === null || this.geocodedLocations === null) {
            return;
          }
          const { geocodedLocationsGeoJson } = this;

          const dataSourceName = 'geocodedLocations';
          if (this.map.getSource(dataSourceName)) {
            this.map.getSource(dataSourceName).setData(geocodedLocationsGeoJson);
          } else {
            this.map.addSource(dataSourceName, {
              type: 'geojson',
              data: geocodedLocationsGeoJson,
              cluster: true,
              maxzoom: 1000, // Max zoom points on, set very high. Allows us to zoom in on the map without breaking clusters
              clusterMaxZoom: 1000, // Max zoom to cluster points on, set very high
              clusterRadius: this.clusterRadius, // Radius of each cluster when clustering points (defaults to 50)
            });
          }

          const geofenceLayerData = {
            type: 'geojson',
            data: {
              type: 'FeatureCollection',
              features: [],
            },
          };

          if (this.map.getSource('place_polygons_source')) {
            this.map.getSource('place_polygons_source').setData({
              type: 'FeatureCollection',
              features: [],
            });
          } else {
            this.map.addSource('place_polygons_source', geofenceLayerData);
          }

          this.updateGeofenceLayerData();

          this.updateLocationShapesDataSource();

          this.superC = new Supercluster({ radius: this.clusterRadius }).load(geocodedLocationsGeoJson.features);

          if (this.map.getLayer('3d-buildings') == null) {
            this.map.addLayer({
              id: '3d-buildings',
              source: 'composite',
              'source-layer': 'building',
              filter: ['==', 'extrude', 'true'],
              type: 'fill-extrusion',
              minzoom: 15,
              paint: {
                'fill-extrusion-color': '#aaa',
                'fill-extrusion-height': {
                  type: 'identity',
                  property: 'height',
                },
                'fill-extrusion-base': {
                  type: 'identity',
                  property: 'min_height',
                },
                'fill-extrusion-opacity': 0.6,
              },
            });

            this.map.addLayer({
              id: 'place_polygons_fill_layer',
              type: 'fill-extrusion',
              source: 'place_polygons_source',
              paint: {
                'fill-extrusion-opacity': 0.3,
                'fill-extrusion-height': {
                  property: 'extrusion',
                  type: 'identity',
                },
                'fill-extrusion-base': 0,
                'fill-extrusion-color': Colors.primary,
              },
            });

            this.map.addLayer({
              id: 'place_polygons_line_layer',
              type: 'line',
              source: 'place_polygons_source',
              paint: {
                'line-color': Colors.primary,
              },
            });

            this.map.addLayer({
              id: 'clusters',
              type: 'circle',
              source: 'geocodedLocations',
              filter: ['has', 'point_count'],
              paint: {
                'circle-color': {
                  property: 'point_count',
                  type: 'interval',
                  stops: [
                    [0, Colors.secondary],
                    [100, Colors.secondary],
                    [750, Colors.secondary],
                  ],
                },
                'circle-stroke-color': '#ffffff',
                'circle-stroke-width': 2,
                'circle-stroke-opacity': 1,
                'circle-radius': {
                  property: 'point_count',
                  type: 'interval',
                  stops: [
                    [0, 20],
                    [100, 30],
                    [750, 40],
                  ],
                },
              },
            });

            this.map.addLayer({
              id: 'cluster-count',
              type: 'symbol',
              source: 'geocodedLocations',
              filter: ['has', 'point_count'],
              layout: {
                'text-field': '{point_count_abbreviated}',
                'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Regular'],
                'text-size': 14,
              },
              paint: {
                'text-color': '#ffffff',
              },
            });

            this.map.addLayer({
              id: 'unclustered-point',
              type: 'circle',
              source: 'geocodedLocations',
              filter: ['!has', 'point_count'],
              paint: {
                'circle-color': Colors.secondary,
                'circle-radius': 8,
                'circle-stroke-width': 1,
                'circle-stroke-color': '#fff',
              },
            });

            this.map.addLayer({
              id: 'location_shapes_fill_layer',
              type: 'fill',
              source: 'location_shapes_source',
              paint: {
                'fill-opacity': 0.3,
                'fill-color': Colors.primary,
              },
            });

            this.map.addLayer({
              id: 'location_shapes_line_layer',
              type: 'line',
              source: 'location_shapes_source',
              paint: {
                'line-color': Colors.primary,
              },
            });

            this.map.on('click', 'clusters', (e) => {
              const latLng = e.features[0].geometry.coordinates;
              const currentZoom = parseInt(this.map.getZoom(), 10);
              const thisCluster = this.superC.getClusters(
                [e.lngLat.lng - 1, e.lngLat.lat - 1, e.lngLat.lng + 1, e.lngLat.lat + 1],
                currentZoom,
              );
              let clusterId;
              for (let i = 0; i < thisCluster.length; i += 1) {
                if (thisCluster[i].properties) {
                  if (thisCluster[i].properties.cluster) {
                    clusterId = thisCluster[i].properties.cluster_id;
                  }
                }
              }

              if (clusterId || clusterId === 0) {
                const zoom = this.superC.getClusterExpansionZoom(clusterId, currentZoom) + 1;
                this.map.flyTo({ center: latLng, zoom, speed: 1.5 });
              } else {
                const inventoryCluster = e.features[0];
                this.map
                  .getSource('geocodedLocations')
                  .getClusterLeaves(inventoryCluster.properties.cluster_id, Infinity, 0, (error, features) => {
                    this.showClusterPopupAtLocation(
                      inventoryCluster.geometry.coordinates[1],
                      inventoryCluster.geometry.coordinates[0],
                      features,
                    );
                  });
              }
            });

            this.map.on('click', 'unclustered-point', (e) => {
              const inventory = e.features[0];
              this.showClusterPopupAtLocation(inventory.geometry.coordinates[1], inventory.geometry.coordinates[0], [
                inventory,
              ]);
            });
          }

          if (this.fitToBounds || forceFitToBounds) {
            this.zoomToBoundingBox();
          }

          if (!this.allowMouseScrollZooming) {
            this.map.scrollZoom.disable();
          }
        }, 250);
      },
      zoomToBoundingBox() {
        if (this.map != null && this.boundingBox != null) {
          this.map.stop();

          if (this.boundingBox[0][0] === this.boundingBox[1][0] && this.boundingBox[0][1] === this.boundingBox[1][1]) {
            this.map.easeTo({
              center: [this.boundingBox[0][0], this.boundingBox[0][1]],
              zoom: 16, // Zoom in on single point locations.
            });
          } else {
            this.map.fitBounds(this.boundingBox, {
              duration: 500,
              padding: {
                top: 50,
                bottom: 50,
                left: 50,
                right: 50,
              },
            });
          }
        }
      },
      updateGeofenceLayerData() {
        if (!this.map || !this.map.getSource('place_polygons_source')) {
          return;
        }
        const geofences =
          this.geofenceGroups != null
            ? this.geofenceGroups.map((gg) => gg.geofences).reduce((mem, f) => [...mem, ...f], [])
            : [];
        const features = geofences.map((g) => g.feature);
        this.map.getSource('place_polygons_source').setData({
          type: 'FeatureCollection',
          features,
        });

        if (features.length > 0) {
          const minLat = geofences.map((x) => parseFloat(x.boundsSwLat)).reduce((a, b) => Math.min(a, b), 90);
          const maxLat = geofences.map((x) => parseFloat(x.boundsNeLat)).reduce((a, b) => Math.max(a, b), -90);
          const minLng = geofences.map((x) => parseFloat(x.boundsSwLng)).reduce((a, b) => Math.min(a, b), 180);
          const maxLng = geofences.map((x) => parseFloat(x.boundsNeLng)).reduce((a, b) => Math.max(a, b), -180);
          const marginV = getContainerWidth('map-container') / 5;
          const marginH = getContainerHeight('map-container') / 5;

          this.map.fitBounds(
            [
              [minLng, minLat],
              [maxLng, maxLat],
            ],
            {
              padding: {
                left: marginH,
                right: marginH,
                top: marginV,
                bottom: marginV,
              },
              duration: 0,
            },
          );
        }
      },
      updateLocationShapesDataSource() {
        if (this.map == null) {
          return;
        }

        const data = {
          type: 'FeatureCollection',
          features: this.locationShapes != null ? this.locationShapes.map((x) => x.feature) : [],
        };
        if (this.map.getSource('location_shapes_source')) {
          this.map.getSource('location_shapes_source').setData(data);
        } else {
          this.map.addSource('location_shapes_source', { type: 'geojson', data });
        }
      },
      showClusterPopupAtLocation(lat, lng, inventoryCluster) {
        if (!(this.lastPopup === null)) {
          this.lastPopup.remove();
        }
        this.currentClusterInventory = inventoryCluster.map((inv) => inv.properties);
        setTimeout(() => {
          const html = window.document.getElementById('inventory_cluster_popup_content').innerHTML;
          this.lastPopup = new mapboxgl.Popup().setLngLat([lng, lat]).setHTML(html).addTo(this.map);
        }, 0); // allow cluster to update
      },
      handlePolygonCreation(e) {
        if (this.initialPolygonMapboxDraw) {
          const id = this.initialPolygonMapboxDraw[0];
          this.mapboxDraw.delete(id);
          this.initialPolygonMapboxDraw = null;
        }
        if (
          this.mode !== null ||
          (this.mapboxDraw != null &&
            this.mapboxDraw.getSelected() != null &&
            this.mapboxDraw.getSelected().features.length > 0)
        ) {
          return;
        }
        this.mapPolygonControl.deactivate();
        this.initialPolygonMapboxDraw = this.mapboxDraw.getSelected().features.map((x) => x.id);
        this.$emit('polygonCreated', e.features);
        this.updateDeleteGeofenceButtonVisibility();
      },
      handlePolygonDeletion(e) {
        this.$emit('polygonDeleted', e.features);
        this.updateDeleteGeofenceButtonVisibility();
      },
      handlePolygonUpdate(e) {
        this.$emit('polygonUpdated', e.features);
      },
      updateDeleteGeofenceButtonVisibility() {
        const btn = window.document.getElementsByClassName('mapbox-gl-draw_trash')[0];
        if (btn != null) {
          btn.style = this.initialPolygon == null ? 'display: none;' : '';
        }
      },
    },
  };
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @use '@/styles/variables' as *;

  .map-container {
    display: flex;
    margin: -1.6rem -1.6rem 0 -1.6rem;
    position: relative;

    .loader-container {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      display: flex;
      z-index: 1;

      > div {
        margin: auto;
        padding: 1.6rem 3.2rem;
        background: #fff;
        box-shadow: $small-shadow;
        border-radius: 0.8rem;
      }
    }
  }

  .map {
    width: 100%;
    margin: auto;
    height: 40rem;
    background: #fff;
  }

  .site-popup-content {
    .inventory-table-container {
      overflow-y: auto;
      max-height: 15rem;
      height: auto;
      min-width: 28rem;

      table {
        thead {
          th {
            padding: 0.1rem 1rem 0.1rem 0;
          }
        }

        tbody {
          td {
            font-weight: 400;
            font-size: 0.9em;
            padding: 0.1rem 1rem 0.1rem 0;
          }
        }
      }
    }
  }
</style>
